/**
 * 문의하기 시트 (하단 슬라이드업)
 * "새 문의"와 "문의 내역" 탭으로 구성됩니다.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery } from "@apollo/client";
import { X, Send, ImagePlus, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { createLogger } from "@ict/shared";
import { CONTACT_STATUS_COLORS, type ContactIssueItem } from "@ict/shared/graphql/queries/contact";
import { useDeviceInfo } from "@/hooks/useDeviceInfo";
import { useToast } from "@/contexts/ToastContext";
import { captureScreenshot } from "@/lib/screenshot";
import {
  CREATE_CONTACT_ISSUE,
  MY_CONTACT_ISSUES,
  CANCEL_CONTACT_ISSUE,
} from "@/lib/graphql/contact";
import { uploadContactImage } from "@/lib/upload";

const logger = createLogger({ module: "ContactSheet" });

interface ContactSheetProps {
  onClose: () => void;
}

type Tab = "new" | "history";

/** 상태 뱃지 */
function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${CONTACT_STATUS_COLORS[status] || "bg-slate-100 text-slate-600"}`}
    >
      {status}
    </span>
  );
}

export function ContactSheet({ onClose }: ContactSheetProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>("new");

  // 새 문의 상태
  const [description, setDescription] = useState("");
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [attachmentUrls, setAttachmentUrls] = useState<string[]>([]);
  const [showMetadata, setShowMetadata] = useState(false);
  const [screenshotUploading, setScreenshotUploading] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const deviceInfo = useDeviceInfo();
  const { showToast } = useToast();

  // GraphQL
  const [createIssue, { loading: creating }] = useMutation(CREATE_CONTACT_ISSUE);
  const [cancelIssue] = useMutation(CANCEL_CONTACT_ISSUE);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const {
    data: issuesData,
    loading: loadingIssues,
    refetch: refetchIssues,
  } = useQuery(MY_CONTACT_ISSUES, {
    fetchPolicy: "cache-and-network",
    skip: activeTab !== "history",
  });

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(onClose, 200);
  }, [onClose]);

  // 등장 애니메이션
  useEffect(() => {
    requestAnimationFrame(() => setIsVisible(true));
  }, []);

  // 시트 열릴 때 스크린샷 캡처 → 즉시 업로드
  useEffect(() => {
    async function captureAndUpload() {
      const base64 = await captureScreenshot();
      if (!base64) {
        logger.warn("스크린샷 캡처 실패: base64 null");
        return;
      }

      logger.info(`스크린샷 캡처 완료: ${Math.round(base64.length / 1024)}KB`);

      // 백그라운드 업로드 (완료까지 전송 버튼 비활성화)
      setScreenshotUploading(true);
      try {
        const result = await uploadContactImage(base64, `screenshot-${Date.now()}.jpg`);
        logger.info("스크린샷 업로드 결과:", result);
        if (result.success && result.url) {
          setScreenshotUrl(result.url);
        } else {
          logger.warn("스크린샷 업로드 실패:", result.error, result.errorCode);
        }
      } finally {
        setScreenshotUploading(false);
      }
    }
    captureAndUpload();
  }, []);

  // ESC 키로 닫기
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleClose]);

  // 뒤로가기 시 닫기
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      handleClose();
    };
    document.addEventListener("closeTopModal", handler);
    return () => document.removeEventListener("closeTopModal", handler);
  }, [handleClose]);

  // 사진 첨부 핸들러 → 즉시 업로드
  const handleAttachPhoto = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files) return;

      for (const file of Array.from(files)) {
        try {
          setUploadingCount((prev) => prev + 1);
          const result = await uploadContactImage(file);
          if (result.success && result.url) {
            setAttachmentUrls((prev) => [...prev, result.url!]);
          } else {
            showToast("사진 업로드에 실패했습니다.", "error");
            logger.error("사진 업로드 실패:", result.error);
          }
        } catch (error) {
          logger.error("사진 첨부 실패:", error);
        } finally {
          setUploadingCount((prev) => prev - 1);
        }
      }
      e.target.value = "";
    },
    [showToast]
  );

  // 문의 전송
  const handleSubmit = useCallback(async () => {
    if (!description.trim()) {
      showToast("문의 내용을 입력해주세요.", "warning");
      return;
    }

    if (!navigator.onLine) {
      showToast("인터넷 연결이 필요합니다.", "error");
      return;
    }

    try {
      const metadata = JSON.stringify({
        페이지: deviceInfo.currentPath,
        사용자: deviceInfo.userName,
        회사: deviceInfo.companyName,
        플랫폼: deviceInfo.platform,
        제조사: deviceInfo.manufacturer || undefined,
        기기: deviceInfo.model || "웹 브라우저",
        OS: deviceInfo.osVersion || navigator.userAgent.slice(0, 50),
        앱버전: deviceInfo.appVersion || "웹",
        앱빌드: deviceInfo.appBuild || undefined,
        앱ID: deviceInfo.appId || undefined,
        네트워크: deviceInfo.networkType || undefined,
        배터리:
          deviceInfo.batteryLevel >= 0
            ? `${deviceInfo.batteryLevel}%${deviceInfo.isCharging ? " (충전중)" : ""}`
            : undefined,
        해상도: deviceInfo.screenResolution,
        언어: deviceInfo.language || undefined,
        시뮬레이터: deviceInfo.isVirtual ? "예" : undefined,
      });

      await createIssue({
        variables: {
          input: {
            description: description.trim(),
            screenshot: screenshotUrl || undefined,
            attachments: attachmentUrls.length > 0 ? attachmentUrls : undefined,
            metadata,
          },
        },
      });

      showToast("문의가 접수되었습니다.", "success");
      setDescription("");
      setScreenshotUrl(null);
      setAttachmentUrls([]);
      setActiveTab("history");
      refetchIssues();
    } catch (error) {
      logger.error("문의 전송 실패:", error);
      showToast("문의 전송에 실패했습니다. 다시 시도해주세요.", "error");
    }
  }, [
    description,
    screenshotUrl,
    attachmentUrls,
    deviceInfo,
    createIssue,
    showToast,
    refetchIssues,
  ]);

  const handleCancel = useCallback(
    async (identifier: string) => {
      if (!window.confirm("문의를 취소하시겠습니까?")) return;

      setCancellingId(identifier);
      try {
        await cancelIssue({ variables: { identifier } });
        showToast("문의가 취소되었습니다.", "success");
        refetchIssues();
      } catch (error) {
        logger.error("문의 취소 실패:", error);
        showToast("문의 취소에 실패했습니다.", "error");
      } finally {
        setCancellingId(null);
      }
    },
    [cancelIssue, showToast, refetchIssues]
  );

  const issues = issuesData?.myContactIssues || [];

  return createPortal(
    <div
      className={`fixed inset-0 z-[150] flex flex-col justify-end transition-all duration-200 ${
        isVisible ? "bg-black/50" : "bg-black/0"
      }`}
      onClick={handleClose}
    >
      {/* 시트 본체 */}
      <div
        className={`html2canvas-ignore flex max-h-[85vh] flex-col rounded-t-2xl bg-white shadow-xl transition-all duration-200 ${
          isVisible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <h2 className="text-lg font-semibold text-slate-900">문의하기</h2>
          <button
            type="button"
            onClick={handleClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100"
            aria-label="닫기"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* 탭 */}
        <div className="flex border-b border-slate-100">
          <button
            type="button"
            onClick={() => setActiveTab("new")}
            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
              activeTab === "new" ? "border-b-2 border-blue-500 text-blue-600" : "text-slate-500"
            }`}
          >
            새 문의
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveTab("history");
              refetchIssues();
            }}
            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
              activeTab === "history"
                ? "border-b-2 border-blue-500 text-blue-600"
                : "text-slate-500"
            }`}
          >
            문의 내역
          </button>
        </div>

        {/* 콘텐츠 */}
        <div className="flex-1 overflow-auto p-4">
          {activeTab === "new" ? (
            <div className="space-y-4">
              {/* 스크린샷 미리보기 */}
              {/* 문의 내용 (필수) */}
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="문의 내용을 입력해주세요 *"
                rows={4}
                className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />

              {/* 추가 첨부 */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingCount > 0}
                  className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50"
                >
                  {uploadingCount > 0 ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ImagePlus className="h-4 w-4" />
                  )}
                  {uploadingCount > 0 ? "업로드 중..." : "사진 첨부"}
                </button>
                {attachmentUrls.length > 0 && (
                  <span className="text-xs text-slate-400">{attachmentUrls.length}장 첨부됨</span>
                )}
              </div>

              {/* 첨부 이미지 미리보기 */}
              {attachmentUrls.length > 0 && (
                <div className="flex gap-2 overflow-x-auto">
                  {attachmentUrls.map((url, idx) => (
                    <div key={idx} className="relative shrink-0">
                      <img
                        src={url}
                        alt={`첨부 ${idx + 1}`}
                        className="h-16 w-16 rounded-lg border border-slate-200 object-cover"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setAttachmentUrls((prev) => prev.filter((_, i) => i !== idx))
                        }
                        className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-slate-700 text-white"
                        aria-label="첨부 제거"
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* 자동 수집 정보 (접기/펼치기) */}
              <button
                type="button"
                onClick={() => setShowMetadata((prev) => !prev)}
                className="flex items-center gap-1 text-xs text-slate-400"
              >
                {showMetadata ? (
                  <ChevronUp className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
                자동 수집 정보
              </button>
              {showMetadata && (
                <div className="space-y-0.5 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
                  <p>페이지: {deviceInfo.currentPath}</p>
                  <p>사용자: {deviceInfo.userName}</p>
                  <p>회사: {deviceInfo.companyName}</p>
                  <p>플랫폼: {deviceInfo.platform}</p>
                  {deviceInfo.manufacturer && <p>제조사: {deviceInfo.manufacturer}</p>}
                  {deviceInfo.model && <p>기기: {deviceInfo.model}</p>}
                  {deviceInfo.osVersion && <p>OS: {deviceInfo.osVersion}</p>}
                  <p>
                    앱: {deviceInfo.appVersion || "웹"}
                    {deviceInfo.appBuild ? ` (${deviceInfo.appBuild})` : ""}
                  </p>
                  {deviceInfo.appId && <p>패키지: {deviceInfo.appId}</p>}
                  <p>네트워크: {deviceInfo.networkType}</p>
                  <p>해상도: {deviceInfo.screenResolution}</p>
                </div>
              )}

              {/* 전송 버튼 */}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={
                  creating || uploadingCount > 0 || screenshotUploading || !description.trim()
                }
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-500 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-600 active:bg-blue-700 disabled:bg-slate-300"
              >
                <Send className="h-4 w-4" />
                {creating ? "전송 중..." : "문의 보내기"}
              </button>
            </div>
          ) : (
            /* 문의 내역 탭 */
            <div>
              {loadingIssues ? (
                <div className="flex items-center justify-center py-8">
                  <span className="text-sm text-slate-400">불러오는 중...</span>
                </div>
              ) : issues.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <p className="text-sm text-slate-400">문의 내역이 없습니다</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {issues.map((issue: ContactIssueItem) => (
                    <div
                      key={issue.identifier}
                      className="rounded-lg border border-slate-100 px-3 py-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">{issue.identifier}</span>
                            <StatusBadge status={issue.status} />
                          </div>
                          {["접수", "처리중"].includes(issue.status) && (
                            <button
                              type="button"
                              onClick={() => handleCancel(issue.identifier)}
                              disabled={cancellingId === issue.identifier}
                              className="text-xs text-slate-400 hover:text-red-500 transition-colors disabled:opacity-50"
                            >
                              {cancellingId === issue.identifier ? "취소 중..." : "취소"}
                            </button>
                          )}
                        </div>
                        <p className="mt-1 whitespace-pre-line text-sm text-slate-700">
                          {issue.description}
                        </p>
                        <p className="mt-1 text-xs text-slate-400">
                          {new Date(issue.createdAt).toLocaleDateString("ko-KR")}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Safe Area 하단 패딩 */}
        <div className="safe-area-bottom" />

        {/* 숨겨진 파일 입력 (시트 본체 안에 배치하여 backdrop 클릭 방지) */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleAttachPhoto}
          className="hidden"
        />
      </div>
    </div>,
    document.body
  );
}
